import { useQueryClient } from '@tanstack/react-query';
import { useSnackbars } from '@customerio/pluma-components/react';
import { hydraUiApi, useHydraUIAPIMutation, useHydraUIAPIQuery } from '@/services/api';
import { useAnalytics } from '@/services/analytics';

// The merge-toggle events only; intentionally narrower than every workspace
// event so callers passing no properties (`{}`) stay valid even as other
// workspace events (e.g. comparePlansClicked) gain required properties.
type WorkspaceAnalyticsEvent =
  | 'anonymousEventsMergeEnabled'
  | 'anonymousEventsMergeDisabled'
  | 'autoMergeEnabled'
  | 'autoMergeDisabled'
  | 'autoMergeOnUpdateEnabled'
  | 'autoMergeOnUpdateDisabled'
  | 'preserveUnsubscribesOnMergeEnabled'
  | 'preserveUnsubscribesOnMergeDisabled';

interface MergeSettingDefinition {
  key: string;
  get: (environmentId: string, signal: AbortSignal) => Promise<{ enabled: boolean }>;
  update: (environmentId: string, enabled: boolean) => Promise<unknown>;
  enabledEvent: WorkspaceAnalyticsEvent;
  disabledEvent: WorkspaceAnalyticsEvent;
}

const mergeSettingKeys = {
  detail: (workspaceId: number, key: string) => ['workspace-merge-setting', workspaceId, key] as const,
};

const ANONYMOUS_EVENTS_MERGE: MergeSettingDefinition = {
  key: 'anonymous_events_merge',
  get: (environmentId, signal) =>
    hydraUiApi.workspaceSettings.getAnonymousEventsMerge({
      params: { path: { environment_id: environmentId } },
      signal,
    }),
  update: (environmentId, enabled) =>
    hydraUiApi.workspaceSettings.createAnonymousEventsMerge({
      params: { path: { environment_id: environmentId } },
      body: { enabled },
    }),
  enabledEvent: 'anonymousEventsMergeEnabled',
  disabledEvent: 'anonymousEventsMergeDisabled',
};

const AUTO_MERGE: MergeSettingDefinition = {
  key: 'auto_merge',
  get: (environmentId, signal) =>
    hydraUiApi.workspaceSettings.getAutoMerge({
      params: { path: { environment_id: environmentId } },
      signal,
    }),
  update: (environmentId, enabled) =>
    hydraUiApi.workspaceSettings.createAutoMerge({
      params: { path: { environment_id: environmentId } },
      body: { enabled },
    }),
  enabledEvent: 'autoMergeEnabled',
  disabledEvent: 'autoMergeDisabled',
};

const AUTO_MERGE_ON_UPDATE: MergeSettingDefinition = {
  key: 'auto_merge_on_update',
  get: (environmentId, signal) =>
    hydraUiApi.workspaceSettings.getAutoMergeOnUpdate({
      params: { path: { environment_id: environmentId } },
      signal,
    }),
  update: (environmentId, enabled) =>
    hydraUiApi.workspaceSettings.createAutoMergeOnUpdate({
      params: { path: { environment_id: environmentId } },
      body: { enabled },
    }),
  enabledEvent: 'autoMergeOnUpdateEnabled',
  disabledEvent: 'autoMergeOnUpdateDisabled',
};

const PRESERVE_UNSUBSCRIBES_ON_MERGE: MergeSettingDefinition = {
  key: 'preserve_unsubscribes_on_merge',
  get: (environmentId, signal) =>
    hydraUiApi.workspaceSettings.getPreserveUnsubscribesOnMerge({
      params: { path: { environment_id: environmentId } },
      signal,
    }),
  update: (environmentId, enabled) =>
    hydraUiApi.workspaceSettings.createPreserveUnsubscribesOnMerge({
      params: { path: { environment_id: environmentId } },
      body: { enabled },
    }),
  enabledEvent: 'preserveUnsubscribesOnMergeEnabled',
  disabledEvent: 'preserveUnsubscribesOnMergeDisabled',
};

export interface MergeSettingState {
  enabled: boolean;
  isLoading: boolean;
  isSaving: boolean;
  /**
   * True once the read succeeded. `enabled` is `false` both while the read is in
   * flight and after it fails, so a caller that states the setting in prose has
   * to wait for this rather than trust `enabled` alone.
   */
  hasLoaded: boolean;
  toggle: () => void;
}

function useMergeSetting(
  definition: MergeSettingDefinition,
  workspaceId: number | null | undefined,
): MergeSettingState {
  const queryClient = useQueryClient();
  const snackbars = useSnackbars();
  const analytics = useAnalytics();

  const isWorkspaceReady = workspaceId != null && workspaceId > 0;
  const queryKey = mergeSettingKeys.detail(workspaceId ?? 0, definition.key);

  const query = useHydraUIAPIQuery({
    queryKey,
    queryFn: ({ signal }) => definition.get(String(workspaceId), signal),
    queryConfig: {
      enabled: isWorkspaceReady,
      staleTime: 300_000,
      refetchOnWindowFocus: false,
    },
  });

  const mutation = useHydraUIAPIMutation(
    (nextEnabled: boolean) => definition.update(String(workspaceId), nextEnabled),
    {
      onSuccess: (_data, nextEnabled) => {
        queryClient.setQueryData(queryKey, { enabled: nextEnabled });
        void analytics.track('workspace', nextEnabled ? definition.enabledEvent : definition.disabledEvent, {});
      },
      onError: () => {
        snackbars.create({ message: 'Your changes could not be saved. Please try again.', variant: 'critical' });
      },
    },
  );

  return {
    enabled: query.data?.enabled ?? false,
    isLoading: query.isLoading,
    isSaving: mutation.isPending,
    hasLoaded: query.isSuccess,
    toggle: () => {
      if (!isWorkspaceReady) return;
      mutation.mutate(!(query.data?.enabled ?? false));
    },
  };
}

/**
 * Mirrors Ember's `environment.current.hasMultipleIdentifiers`: a workspace is
 * eligible for profile merge only when it uses the multi-identifier scheme with
 * both `id` and `email` enabled. Identifier state `0` means disabled (see
 * `IdentifierState.Disabled` in the Ember `utils/identifiers`).
 */
export function useHasMultipleIdentifiers(workspaceId: number | null | undefined) {
  const query = useHydraUIAPIQuery({
    queryKey: ['getEnvironment', { params: { environmentId: workspaceId ?? 0 } }],
    queryFn: ({ signal }) =>
      hydraUiApi.environments.get({
        params: { path: { environment_id: String(workspaceId) } },
        signal,
      }),
    queryConfig: {
      enabled: workspaceId != null && workspaceId > 0,
      staleTime: 60_000,
      select: (data) => {
        const environment = data.environment;
        const identifiers = environment?.identifiers;
        return (
          environment?.identifier_type === 'multi' &&
          identifiers != null &&
          identifiers.id !== 0 &&
          identifiers.email !== 0
        );
      },
    },
  });

  return { hasMultipleIdentifiers: query.data ?? false, isLoading: query.isLoading };
}

export function useAnonymousEventsMergeSetting(workspaceId: number | null | undefined) {
  return useMergeSetting(ANONYMOUS_EVENTS_MERGE, workspaceId);
}

export function useAutoMergeSetting(workspaceId: number | null | undefined) {
  return useMergeSetting(AUTO_MERGE, workspaceId);
}

export function useAutoMergeOnUpdateSetting(workspaceId: number | null | undefined) {
  return useMergeSetting(AUTO_MERGE_ON_UPDATE, workspaceId);
}

export function usePreserveUnsubscribesOnMergeSetting(workspaceId: number | null | undefined) {
  return useMergeSetting(PRESERVE_UNSUBSCRIBES_ON_MERGE, workspaceId);
}
